import { useAxiosConfig } from '@shared/api/AxiosConfigProvider';
import { refreshQueries } from '@shared/api/hooks/refresh-queries';
import {
  getPurchasedGoodsPackagingListQueryKey,
  getPurchasedGoodsPackagingRecordsListQueryKey,
  usePurchasedGoodsDestroy,
  usePurchasedGoodsPackagingCoverageRetrieve,
  usePurchasedGoodsPackagingList,
  usePurchasedGoodsPackagingPartialUpdate,
  usePurchasedGoodsPackagingPreviewRetrieve,
  usePurchasedGoodsPackagingRecordsList,
  usePurchasedGoodsPackagingRecordsPartialUpdate,
  usePurchasedGoodsPackagingRecordsUpdate,
  usePurchasedGoodsPackagingRetrieve,
  usePurchasedGoodsPackagingSpendRetrieve,
  usePurchasedGoodsPackagingUpdate,
  usePurchasedGoodsRecordsDestroy,
} from '@shared/api/lib/purchased-goods/purchased-goods';

import {
  ActivityCoverage,
  PackagingActivityRecordUpdate,
  PackagingActivityRetrieve,
  PackagingActivityUpdate,
  PaginatedPackagingActivitiesListList,
  PaginatedPackagingActivityRecordList,
  PurchasedGoodsPackagingListParams,
  PurchasedGoodsPackagingPreviewRetrieveParams,
  PurchasedGoodsPackagingRecordsListParams,
  PurchasedGoodsPackagingSpendRetrieveParams,
  PurchasedGoodsSpend,
} from '@shared/api/types';
import { useQueryClient } from '@tanstack/react-query';

export const useGetPurchasedPackagingList = (
  params?: PurchasedGoodsPackagingListParams
) => {
  return usePurchasedGoodsPackagingList<PaginatedPackagingActivitiesListList>(
    params,
    {
      request: useAxiosConfig(),
      query: {
        keepPreviousData: true,
      },
    }
  );
};

export const useGetPurchasedPackagingItem = ({
  activityUuid,
}: {
  activityUuid: string;
}) => {
  return usePurchasedGoodsPackagingRetrieve<PackagingActivityRetrieve>(
    activityUuid,
    {
      request: useAxiosConfig(),
    }
  );
};

export const useGetPurchasedPackagingItemSpend = ({
  activityUuid,
  params,
}: {
  activityUuid: string;
  params?: PurchasedGoodsPackagingSpendRetrieveParams;
}) => {
  return usePurchasedGoodsPackagingSpendRetrieve<PurchasedGoodsSpend>(
    activityUuid,
    params,
    {
      request: useAxiosConfig(),
      query: {
        keepPreviousData: true,
      },
    }
  );
};

export const useUpdatePurchasedPackagingItem = () => {
  return usePurchasedGoodsPackagingUpdate<PackagingActivityUpdate>({
    request: useAxiosConfig(),
  });
};

export const usePatchPurchasedPackagingItem = () => {
  return usePurchasedGoodsPackagingPartialUpdate<PackagingActivityUpdate>({
    request: useAxiosConfig(),
  });
};

export const useGetPurchasedPackagingPreview = ({
  params,
  enabled,
}: {
  params?: PurchasedGoodsPackagingPreviewRetrieveParams;
  enabled?: boolean;
}) => {
  return usePurchasedGoodsPackagingPreviewRetrieve(params, {
    request: useAxiosConfig(),
    query: {
      enabled: enabled,
      cacheTime: 0,
      staleTime: 0,
    },
  });
};

export const useDeletePurchasedPackagingItem = () => {
  const queryClient = useQueryClient();
  return usePurchasedGoodsDestroy({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: () =>
        refreshQueries(queryClient, getPurchasedGoodsPackagingListQueryKey()),
    },
  });
};

export const useGetPurchasedPackagingItemRecords = ({
  activityUuid,
  params,
}: {
  activityUuid: string;
  params?: PurchasedGoodsPackagingRecordsListParams;
}) => {
  return usePurchasedGoodsPackagingRecordsList<PaginatedPackagingActivityRecordList>(
    activityUuid,
    params,
    {
      request: useAxiosConfig(),
      query: {
        keepPreviousData: true,
      },
    }
  );
};

export const useUpdatePurchasedPackagingItemRecord = () => {
  const queryClient = useQueryClient();
  return usePurchasedGoodsPackagingRecordsUpdate<PackagingActivityRecordUpdate>(
    {
      request: useAxiosConfig(),
      mutation: {
        onSuccess: async (_, variables) => {
          await queryClient.refetchQueries(
            getPurchasedGoodsPackagingRecordsListQueryKey(
              variables.activityUuid
            )
          );
        },
      },
    }
  );
};

export const usePatchPurchasedPackagingItemRecord = () => {
  const queryClient = useQueryClient();
  return usePurchasedGoodsPackagingRecordsPartialUpdate<PackagingActivityRecordUpdate>(
    {
      request: useAxiosConfig(),
      mutation: {
        onSuccess: (_, variables) => {
          queryClient.refetchQueries(
            getPurchasedGoodsPackagingRecordsListQueryKey(
              variables.activityUuid
            )
          );
        },
      },
    }
  );
};

export const useDeletePurchasedPackagingItemRecord = () => {
  const queryClient = useQueryClient();
  return usePurchasedGoodsRecordsDestroy({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: async (_, variables) => {
        await queryClient.refetchQueries(
          getPurchasedGoodsPackagingRecordsListQueryKey(variables.activityUuid)
        );
      },
    },
  });
};

export const useGetPurchasedPackagingCoverage = () => {
  return usePurchasedGoodsPackagingCoverageRetrieve<ActivityCoverage>({
    request: useAxiosConfig(),
  });
};
